import React, { createContext, useState, useEffect, useCallback } from "react";
import AccountService from "../Services/accountService";

const INACTIVITY_TIMEOUT = 20 * 60 * 1000;
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000;

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  let inactivityTimer = null;

  const isAuthPage = () => {
    const path = window.location.pathname;
    return path.startsWith("/login") || path.startsWith("/signup");
  };

  const saveToken = (newToken, newExpireOn, rememberMe) => {
    setToken(newToken);
    localStorage.setItem("token", newToken);
    localStorage.setItem("rememberMe", rememberMe);
    localStorage.setItem("expireOn", new Date(newExpireOn));
    setIsAuthenticated(!!newToken);
  };

  const logout = useCallback(async () => {
    if (isAuthPage()) return;
    await AccountService.Logout();
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem("token");
    clearTimeout(inactivityTimer);
    window.location.href = "/login?logout";
  }, [inactivityTimer]);

  const refreshToken = useCallback(async () => {
    try {
      if (!token) throw new Error("Pas de token disponible");

      const d = await AccountService.KeepAlive();
      if (!d) throw new Error("Échec du renouvellement du token");
      localStorage.setItem("expireOn", d);
    } catch (error) {
      logout();
    }
  }, [token, logout]);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    inactivityTimer = setTimeout(() => {
      const expireOnRaw = localStorage.getItem("expireOn");
      const now = Date.now();

      if (expireOnRaw) {
        const expireOn = new Date(expireOnRaw).getTime();
        if (expireOn < now) {
          logout();
        }
      }
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  useEffect(() => {
    if (isAuthenticated && !isAuthPage()) {
      window.addEventListener("mousemove", resetInactivityTimer);
      window.addEventListener("keydown", resetInactivityTimer);
      window.addEventListener("click", resetInactivityTimer);

      resetInactivityTimer();
    }

    return () => {
      window.removeEventListener("mousemove", resetInactivityTimer);
      window.removeEventListener("keydown", resetInactivityTimer);
      window.removeEventListener("click", resetInactivityTimer);
    };
  }, [isAuthenticated, resetInactivityTimer]);

  useEffect(() => {
    if (isAuthenticated && isAuthPage()) {
      window.location.href = "/dashboard";
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !isAuthPage()) {
      const interval = setInterval(() => {
        refreshToken();
      }, TOKEN_REFRESH_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, refreshToken]);

  return <AuthContext.Provider value={{ token, isAuthenticated, saveToken, logout }}>{children}</AuthContext.Provider>;
};
